import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCurrentUser } from '../../data/mockData';
import AccountSidebar from '../../components/AccountSidebar';
import { request } from '../../utils/request';
import { 
  HeartIcon,
  TrashIcon,
  ShareIcon,
  EyeIcon,
  BookmarkIcon
} from '@heroicons/react/24/outline';

// Interface definitions
interface SavedDesign {
  id: number;
  name: string;
  category: string | CategoryItem;
  savedDate: string;
  image: string;
  isPublic: boolean;
  views: number;
  likes: number;
  code?: string;
  designData?: any;
  productId?: number;
  templateId?: number;
  product?: {
    id: number;
    name: string;
    category: string;
    basePrice: number;
  };
  template?: {
    id: number;
    name: string;
    category: string;
  };
}

interface SavedDesignsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    designs: SavedDesign[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  };
}

interface CategoryItem {
  id: number;
  name: string;
  icon: string | null;
  href: string;
}

interface CategoriesResponse {
  success: boolean;
  code: number;
  message: string;
  result: CategoryItem[];
}

const SavedDesigns: React.FC = () => {
  const [user] = useState(getCurrentUser());
  const [designs, setDesigns] = useState<SavedDesign[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  // Load saved designs from API
  const loadSavedDesigns = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });
      
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      
      const response: SavedDesignsResponse = await request({
        url: `saved-designs?${params.toString()}`,
        method: 'GET'
      });
      console.log('response', response);
      if (response.success) {
        setDesigns(response.result.designs);
        setPagination(response.result.pagination);
      } else {
        setError(response.message || 'Failed to load saved designs');
      }
    } catch (error: any) {
      console.error('Load saved designs error:', error);
      setError('Failed to load saved designs');
    } finally {
      setIsLoading(false);
    }
  };

  // Load categories from API
  const loadCategories = async () => {
    if (!user) return;
    
    try {
      const response: CategoriesResponse = await request({
        url: 'saved-designs/categories',
        method: 'GET'
      });
      
      if (response.success) {
        setCategories(response.result);
      }
    } catch (error: any) {
      console.error('Load categories error:', error);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    loadSavedDesigns();
    loadCategories();
  }, [selectedCategory, pagination.page]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Please log in</h2>
          <Link to="/" className="hover:opacity-80" style={{ color: '#2e3a47' }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  const allCategories = ['all', ...categories.map(cat => cat.name)];

  const handleDeleteDesign = async (designId: number) => {
    try {
      const response: any = await request({
        url: `saved-designs/${designId}`,
        method: 'DELETE'
      });
      
      if (response.success) {
        // Reload designs after successful deletion
        loadSavedDesigns();
      } else {
        alert(response.message || 'Failed to delete design');
      }
    } catch (error: any) {
      console.error('Delete design error:', error);
      alert('Failed to delete design');
    }
  };

  const handleTogglePublic = async (designId: number) => {
    try {
      const response: any = await request({
        url: `saved-designs/${designId}/toggle-public`,
        method: 'PUT'
      });
      
      if (response.success) {
        // Reload designs after successful toggle
        loadSavedDesigns();
      } else {
        alert(response.message || 'Failed to update design visibility');
      }
    } catch (error: any) {
      console.error('Toggle design visibility error:', error);
      alert('Failed to update design visibility');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Saved Designs</h1>
          <p className="text-gray-600">Your saved and customized designs</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <AccountSidebar currentPage="saved-designs" />
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Filters and Toolbar */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">
                    {pagination.total} design{pagination.total !== 1 ? 's' : ''} saved
                  </span>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent text-sm"
                    style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                  >
                    {allCategories.map(category => (
                      <option key={category} value={category}>
                        {category === 'all' ? 'All Categories' : category}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'grid' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'grid' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">⊞</span>
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg ${
                      viewMode === 'list' ? '' : 'text-gray-400 hover:text-gray-600'
                    }`}
                    style={viewMode === 'list' ? { backgroundColor: '#f0f2f5', color: '#2e3a47' } : {}}
                  >
                    <span className="text-lg">☰</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {isLoading && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderBottomColor: '#2e3a47' }}></div>
                <p className="text-gray-600">Loading saved designs...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error</h3>
                <p className="text-gray-600 mb-6">{error}</p>
                <button
                  onClick={loadSavedDesigns}
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Designs Grid/List */}
            {!isLoading && !error && viewMode === 'grid' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {designs.map((design) => (
                  <div key={design.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                    <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative">
                      <img src={design.image} alt={design.name} className="w-full h-full object-contain" />
                      <div className="absolute top-2 right-2 flex space-x-1">
                        <button className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-50">
                          <ShareIcon className="w-4 h-4 text-gray-600" />
                        </button>
                        <button 
                          onClick={() => handleDeleteDesign(design.id)}
                          className="w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-red-50"
                        >
                          <TrashIcon className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-gray-900 text-sm line-clamp-2">
                          {design.name}
                        </h3>
                        <span className="text-xs text-gray-500 ml-2">
                          {typeof design.category === 'string' ? design.category : design.category?.name || 'Unknown'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        Saved on {new Date(design.savedDate).toLocaleDateString()}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3 text-xs text-gray-500">
                          <span className="flex items-center">
                            <EyeIcon className="w-3 h-3 mr-1" />
                            {design.views}
                          </span>
                          <span className="flex items-center">
                            <HeartIcon className="w-3 h-3 mr-1" />
                            {design.likes}
                          </span>
                        </div>
                        <button
                          onClick={() => handleTogglePublic(design.id)}
                          className={`px-2 py-1 text-xs rounded-full ${
                            design.isPublic 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {design.isPublic ? 'Public' : 'Private'}
                        </button>
                      </div>
                      <div className="mt-3 space-y-2">
                        {/* <Link
                          to={`/customize/${design.id}?cacheKey=${design.code}`}
                          className="w-full text-white py-2 px-3 rounded-lg transition-colors text-center block text-sm"
                          style={{ backgroundColor: '#2e3a47' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                        >
                          Edit Design
                        </Link> */}
                        <Link
                          to={`/product/${design.id}`}
                          className="w-full border border-gray-300 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors text-center block text-sm"
                        >
                          View Product
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List View */}
            {!isLoading && !error && viewMode === 'list' && (
              <div className="space-y-4">
                {designs.map((design) => (
                  <div key={design.id} className="bg-white rounded-lg shadow-sm p-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <img src={design.image} alt={design.name} className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-900 mb-1">{design.name}</h3>
                            <p className="text-sm text-gray-500 mb-2">
                              {typeof design.category === 'string' ? design.category : design.category?.name || 'Unknown'} • Saved on {new Date(design.savedDate).toLocaleDateString()}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-500">
                              <span className="flex items-center">
                                <EyeIcon className="w-4 h-4 mr-1" />
                                {design.views} views
                              </span>
                              <span className="flex items-center">
                                <HeartIcon className="w-4 h-4 mr-1" />
                                {design.likes} likes
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleTogglePublic(design.id)}
                              className={`px-3 py-1 text-sm rounded-full ${
                                design.isPublic 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {design.isPublic ? 'Public' : 'Private'}
                            </button>
                            <button className="p-2 text-gray-400 hover:text-gray-600">
                              <ShareIcon className="w-4 h-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteDesign(design.id)}
                              className="p-2 text-red-400 hover:text-red-600"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2 mt-3">
                          <Link
                            to={`/customize/${design.id}`}
                            className="text-white py-2 px-4 rounded-lg transition-colors text-sm"
                            style={{ backgroundColor: '#2e3a47' }}
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                          >
                            Edit Design
                          </Link>
                          <Link
                            to={`/product/${design.id}`}
                            className="border border-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                          >
                            View Product
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && designs.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                <div className="flex justify-center mb-4">
                  <BookmarkIcon className="w-16 h-16 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No saved designs</h3>
                <p className="text-gray-600 mb-6">
                  {selectedCategory === 'all' 
                    ? "You haven't saved any designs yet." 
                    : `No saved designs in ${selectedCategory} category.`}
                </p>
                <Link
                  to="/products"
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  Browse Designs
                </Link>
              </div>
            )}

            {/* Pagination */}
            {!isLoading && !error && pagination.totalPages > 1 && (
              <div className="bg-white rounded-lg shadow-sm p-6 mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} designs
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      disabled={pagination.page === 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm text-gray-600">
                      Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <button
                      onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      disabled={pagination.page === pagination.totalPages}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavedDesigns; 